/**
 * De vaste keuzelijsten van de formulieren, één keer.
 *
 * De niveaus moeten dezelfde zijn als in de backend (services/niveaus.py
 * na stap 2); de toevoeging "/1F" en "/2F" hoort bij de naam van het
 * niveau, het is geen tweede niveau.
 */

/** De niveaus waaruit een leerkracht kiest, van laag naar hoog. 3F is de standaardtekst zelf. */
export const NIVEAUS = ['M4', 'E4', 'M5', 'E5', 'M6/1F', 'E6/1F', 'M7/1F', 'E7/1F', 'M8/2F', 'Plus/2F', '3F'];

/** Het laagste niveau: verder vereenvoudigen kan dan niet. */
export const LAAGSTE_NIVEAU = 'M4';

export const LEERJAREN = [
  { value: '4', label: 'Groep 4' },
  { value: '5', label: 'Groep 5' },
  { value: '6', label: 'Groep 6' },
  { value: '7', label: 'Groep 7' },
  { value: '8', label: 'Groep 8' },
  { value: '', label: 'Combinatiegroep' },
];

/**
 * Leeg betekent Nederlands: dan is er geen vertaalknop. Op alfabet. Vertalen
 * doet het taalmodel in elke taal; voorlezen alleen in de talen van
 * backend/app/services/voorlezen.py (MOEDERTALEN), de rest is alleen te lezen.
 * Een taal erbij: hier, en kan Voxtral hem voorlezen, ook daar. Verander geen
 * bestaande naam: die staat zo bij de leerlingen in de database.
 */
export const MOEDERTALEN = ['', 'Arabisch', 'Berbers', 'Bulgaars', 'Chinees', 'Dari', 'Duits', 'Engels', 'Farsi', 'Frans',
  'Grieks', 'Hindi', 'Hongaars', 'Italiaans', 'Koerdisch (Kurmanji)', 'Koerdisch (Sorani)', 'Kroatisch', 'Litouws',
  'Oekraïens', 'Pasjtoe', 'Pools', 'Portugees', 'Roemeens', 'Russisch', 'Somalisch', 'Spaans', 'Tigrinya', 'Turks', 'Urdu'];

export const AANTALLEN_MEERKEUZE = [0, 1, 2, 3, 4, 5, 6, 7, 8];
export const AANTALLEN_OPEN = [0, 1, 2, 3, 4, 5];

export const ILLUSTRATIESTIJLEN = [
  { value: '', label: 'Geen illustratie' },
  { value: 'lijntekening', label: 'Lijntekening' },
  { value: 'foto', label: 'Foto' },
  { value: 'schattig', label: 'Schattig' },
];

/** De vier teksttypen van het referentiekader, met de teksten voor het beheerpaneel. */
export const TEKSTTYPEN = [
  { value: 'informatief', label: 'Informatieve tekst', knop: 'Nieuwe informatieve tekst', beschrijving: 'Korte beschrijving van het onderwerp', kleur: 'var(--blauw)' },
  { value: 'betogend', label: 'Betogende tekst', knop: 'Nieuwe betogende tekst', beschrijving: 'Korte beschrijving van het betoog', kleur: 'var(--paars)' },
  { value: 'instructief', label: 'Instructieve tekst', knop: 'Nieuwe instructieve tekst', beschrijving: 'Korte beschrijving van de instructie', kleur: 'var(--oranje)' },
  { value: 'fictioneel', label: 'Fictionele tekst', knop: 'Nieuwe fictionele tekst', beschrijving: 'Korte beschrijving van het verhaal', kleur: 'var(--goed-3)' },
];

/**
 * De verhalen in vijf deelteksten (Greimas en Propp), twee soorten
 * fictionele tekst met hetzelfde formulier (pages/beheer/onderdelen/
 * VerhaalFormulier.js, de knop Nieuwe fictionele tekst): met een tijdvak
 * historische fictie (stap 17, genre "historische_fictie"), zonder een
 * verhaal van nu (stap 17d, genre "fictie_van_nu"). Tot stap 20 had
 * historische fictie een eigen knop. Een bestaand verhaal plakken kan in het
 * formulier ook nog (een gewone fictionele tekst).
 */
export const HISTORISCHE_FICTIE = { value: 'historische_fictie', label: 'Historische fictie', kleur: 'var(--bruin)' };
export const FICTIE_VAN_NU = { value: 'fictie_van_nu', label: 'Fictie van nu', kleur: 'var(--goed-3)' };

/** De verhalen in vijf deelteksten, met bouwstenen. */
export const VERHAALGENRES = [HISTORISCHE_FICTIE, FICTIE_VAN_NU];
export const isVerhaal = (tekst) => VERHAALGENRES.some(g => g.value === tekst?.genre);

/**
 * Een dakpanset (stap 22): een informatieve tekst in vier deelteksten met
 * bouwstenen, voor een groepje van vier (services/dakpan.py), met een eigen
 * formulier (pages/beheer/onderdelen/DakpanFormulier.js).
 */
export const DAKPAN = { value: 'dakpan', label: 'Dakpanset', knop: 'Nieuwe dakpanset', kleur: 'var(--petrol)' };
/** Elke soort tekst in deelteksten: de verhalen en de dakpanset (voor het label in een lijst). */
export const DEELTEKSTGENRES = [...VERHAALGENRES, DAKPAN];
export const isDakpan = (tekst) => tekst?.genre === DAKPAN.value;
export const heeftDeelteksten = (tekst) => isVerhaal(tekst) || isDakpan(tekst);
export const genreVan = (tekst) => DEELTEKSTGENRES.find(g => g.value === tekst?.genre);

/** De stemmen van Mistral Voxtral; het model spreekt Nederlands met elke stem. */
export const VOORLEESSTEMMEN = [
  { value: 'en_paul_cheerful', label: 'Paul Vrolijk' },
  { value: 'en_paul_happy', label: 'Paul Blij' },
  { value: 'en_paul_neutral', label: 'Paul Neutraal' },
  { value: 'en_paul_confident', label: 'Paul Zelfverzekerd' },
  { value: 'en_paul_excited', label: 'Paul Enthousiast' },
  { value: 'en_paul_sad', label: 'Paul Verdrietig' },
  { value: 'gb_oliver_neutral', label: 'Oliver Neutraal' },
  { value: 'gb_jane_sarcasm', label: 'Jane (vrouw)' },
];
